<template>
  <main class="h-screen overflow-auto bg-gray-50">
    <div class="px-4 sm:px-6 lg:px-8 mt-10 pb-16 max-w-6xl mx-auto">
      <div class="sm:flex sm:items-start sm:justify-between gap-4">
        <div>
          <h1 class="text-base font-semibold leading-6 text-gray-900">Explanations dedupe</h1>
          <p class="mt-1 text-sm text-gray-500">
            Hidden tool — scan <code class="text-xs bg-gray-100 px-1 rounded">explanations</code> for duplicate
            <code class="text-xs bg-gray-100 px-1 rounded">questionId</code> docs. Newest is a hint only; nothing deletes until you confirm.
          </p>
        </div>
        <button
          type="button"
          class="mt-3 sm:mt-0 rounded bg-white px-3 py-1.5 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-40"
          :disabled="loadingCount || scanning"
          @click="refreshCount"
        >
          {{ loadingCount ? 'Counting…' : 'Refresh count' }}
        </button>
      </div>

      <!-- Stats -->
      <div class="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div class="rounded-lg bg-white px-4 py-5 shadow ring-1 ring-black ring-opacity-5">
          <p class="text-sm text-gray-500">Total explanations</p>
          <p class="mt-1 text-2xl font-semibold text-gray-900">{{ totalCount === null ? '—' : totalCount }}</p>
          <p class="mt-1 text-xs text-gray-400">Count aggregation</p>
        </div>
        <div class="rounded-lg bg-white px-4 py-5 shadow ring-1 ring-black ring-opacity-5">
          <p class="text-sm text-gray-500">Scanned</p>
          <p class="mt-1 text-2xl font-semibold text-gray-900">{{ scannedCount }}</p>
          <p class="mt-1 text-xs text-gray-400">{{ scanComplete ? 'Scan complete' : (lastDocId ? 'Resumable' : 'Not started') }}</p>
        </div>
        <div class="rounded-lg bg-white px-4 py-5 shadow ring-1 ring-black ring-opacity-5">
          <p class="text-sm text-gray-500">Duplicate groups</p>
          <p class="mt-1 text-2xl font-semibold text-amber-600">{{ duplicateGroups.length }}</p>
        </div>
        <div class="rounded-lg bg-white px-4 py-5 shadow ring-1 ring-black ring-opacity-5">
          <p class="text-sm text-gray-500">Selected to delete</p>
          <p class="mt-1 text-2xl font-semibold text-red-600">{{ selectedIds.length }}</p>
        </div>
      </div>

      <!-- Actions -->
      <div class="mt-6 flex flex-wrap items-center gap-2">
        <button
          type="button"
          class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-40"
          :disabled="scanning"
          @click="startOrResumeScan"
        >
          {{ scanning ? 'Scanning…' : (canResume ? 'Resume scan' : 'Start scan') }}
        </button>
        <button
          v-if="scanning"
          type="button"
          class="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          @click="stopScan"
        >
          Stop scan
        </button>
        <button
          type="button"
          class="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-40"
          :disabled="scanning || (!lastDocId && scannedCount === 0 && duplicateGroups.length === 0)"
          @click="resetScanProgress"
        >
          Reset progress
        </button>
        <button
          type="button"
          class="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-40"
          :disabled="scanning || deleting || selectedIds.length === 0"
          @click="deleteSelectedBatch"
        >
          {{ deleting ? 'Deleting…' : `Delete selected (${selectedIds.length})` }}
        </button>
      </div>

      <p v-if="scanError" class="mt-3 text-sm text-red-600">{{ scanError }}</p>
      <p v-if="scanning" class="mt-3 text-sm text-gray-500">
        Scanning page by page ({{ PAGE_SIZE }} docs/page). Progress is saved so you can stop and resume.
      </p>

      <!-- Groups -->
      <div class="mt-8 space-y-4">
        <div
          v-for="group in duplicateGroups"
          :key="group.questionId"
          class="rounded-lg bg-white shadow ring-1 ring-black ring-opacity-5 overflow-hidden"
        >
          <div class="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2 bg-gray-50">
            <div>
              <p class="text-sm font-semibold text-gray-900">
                questionId: <span class="font-mono text-xs">{{ group.questionId }}</span>
              </p>
              <p class="text-xs text-gray-500">{{ group.docs.length }} docs</p>
            </div>
            <button
              type="button"
              class="text-xs font-semibold text-indigo-600 hover:text-indigo-500"
              @click="toggleGroup(group.questionId)"
            >
              {{ expanded[group.questionId] ? 'Collapse' : 'Expand' }}
            </button>
          </div>

          <div v-if="expanded[group.questionId]" class="divide-y divide-gray-100">
            <div
              v-for="docItem in group.docs"
              :key="docItem.id"
              class="px-4 py-4 flex gap-3"
              :class="docItem.id === group.recommendedKeepId ? 'bg-emerald-50/60' : ''"
            >
              <input
                type="checkbox"
                class="mt-1 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                :checked="selectedSet.has(docItem.id)"
                :disabled="deleting"
                @change="toggleSelect(docItem.id, $event.target.checked)"
              >
              <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <span class="font-mono text-gray-800">{{ docItem.id }}</span>
                  <span
                    v-if="docItem.id === group.recommendedKeepId"
                    class="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800"
                  >
                    Recommended keep (newest)
                  </span>
                  <span>↑ {{ docItem.upvotes }} · ↓ {{ docItem.downvotes }}</span>
                  <span v-if="docItem.hasTextHtml">has textHtml</span>
                  <span v-else-if="docItem.hasText">text only</span>
                  <span v-else class="text-amber-600">empty body</span>
                </div>
                <div
                  class="mt-2 text-sm text-gray-700 max-h-40 overflow-y-auto border border-gray-100 rounded p-2 bg-white prose prose-sm max-w-none"
                >
                  <div v-if="docItem.textHtml" v-html="docItem.textHtml" />
                  <pre v-else-if="docItem.text" class="whitespace-pre-wrap text-xs font-sans m-0">{{ docItem.text }}</pre>
                  <p v-else class="text-gray-400 italic m-0">No content</p>
                </div>
                <div class="mt-2">
                  <button
                    type="button"
                    class="rounded bg-white px-2.5 py-1 text-xs font-semibold text-red-700 shadow-sm ring-1 ring-inset ring-red-200 hover:bg-red-50 disabled:opacity-40"
                    :disabled="deleting"
                    @click="deleteOne(docItem.id, group.questionId)"
                  >
                    Delete this doc
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p
          v-if="!scanning && scanComplete && duplicateGroups.length === 0"
          class="text-sm text-gray-500 text-center py-8"
        >
          No duplicate questionId groups found in the scanned collection.
        </p>
        <p
          v-else-if="!scanning && !scanComplete && duplicateGroups.length === 0"
          class="text-sm text-gray-500 text-center py-8"
        >
          Start or resume a scan to list duplicate groups.
        </p>
      </div>
    </div>
  </main>
</template>

<script setup>
import { ref, computed, reactive, onMounted, onBeforeUnmount } from 'vue'
import swal from 'sweetalert2'
import { db } from '@/firebase/config.js'
import {
  collection,
  query,
  orderBy,
  startAfter,
  limit,
  getDocs,
  getDoc,
  getCountFromServer,
  deleteDoc,
  doc,
  documentId
} from 'firebase/firestore'

const PAGE_SIZE = 500
const STORAGE_KEY = 'admin:explanations-dedupe:v1'
const EXPLANATIONS = 'explanations'

const totalCount = ref(null)
const loadingCount = ref(false)
const scanning = ref(false)
const stopRequested = ref(false)
const deleting = ref(false)
const scanError = ref('')

const scannedCount = ref(0)
const lastDocId = ref(null)
const scanComplete = ref(false)
/** @type {import('vue').Ref<Array<{ questionId: string, recommendedKeepId: string, docs: Array<any> }>>} */
const duplicateGroups = ref([])
/** Internal map while scanning: questionId -> docs[] (all docs, including singles) */
const allByQuestionId = reactive({})

const selectedSet = ref(new Set())
const expanded = reactive({})

const selectedIds = computed(() => [...selectedSet.value])
const canResume = computed(() => Boolean(lastDocId.value) && !scanComplete.value)

const timestampToMs = (value) => {
  if (!value) return null
  if (typeof value.toMillis === 'function') return value.toMillis()
  if (typeof value.seconds === 'number') return value.seconds * 1000
  return null
}

const docRecencyMs = (raw) => {
  const fromUpdated = timestampToMs(raw.updatedAt)
  if (fromUpdated != null) return fromUpdated
  const fromCreated = timestampToMs(raw.createdAt)
  if (fromCreated != null) return fromCreated
  return null
}

const summarizeDoc = (id, raw) => {
  const text = typeof raw.text === 'string' ? raw.text : ''
  const textHtml = typeof raw.textHtml === 'string' ? raw.textHtml : ''
  return {
    id,
    questionId: raw.questionId || '',
    text,
    textHtml,
    upvotes: Number(raw.upvotes) || 0,
    downvotes: Number(raw.downvotes) || 0,
    hasTextHtml: textHtml.trim().length > 0,
    hasText: text.trim().length > 0,
    recencyMs: docRecencyMs(raw),
    // Firestore auto-ids are roughly time-ordered; used when no timestamp fields exist
    idSortKey: id,
    bodyLoaded: true
  }
}

const pickNewestId = (docs) => {
  const withTs = docs.filter((d) => d.recencyMs != null)
  if (withTs.length > 0) {
    return [...withTs].sort((a, b) => b.recencyMs - a.recencyMs)[0].id
  }
  return [...docs].sort((a, b) => (a.idSortKey < b.idSortKey ? 1 : -1))[0].id
}

const rebuildDuplicateGroupsFromMap = () => {
  const groups = []
  Object.keys(allByQuestionId).forEach((questionId) => {
    const docs = allByQuestionId[questionId]
    if (!questionId || !docs || docs.length < 2) return
    const sorted = [...docs].sort((a, b) => {
      if (a.recencyMs != null && b.recencyMs != null) return b.recencyMs - a.recencyMs
      if (a.recencyMs != null) return -1
      if (b.recencyMs != null) return 1
      return a.idSortKey < b.idSortKey ? 1 : -1
    })
    groups.push({
      questionId,
      recommendedKeepId: pickNewestId(docs),
      docs: sorted
    })
  })
  groups.sort((a, b) => b.docs.length - a.docs.length || a.questionId.localeCompare(b.questionId))
  duplicateGroups.value = groups
}

/**
 * Persist resume cursor + ALL seen questionIds (including singles).
 * Bodies (text/textHtml) are stored only for groups that already have 2+ docs to keep localStorage smaller.
 * Cross-page duplicates need singles in the map; without that, resume would miss pairs split across pages.
 */
const persistProgress = () => {
  const duplicateIdSet = new Set()
  duplicateGroups.value.forEach((g) => {
    g.docs.forEach((d) => duplicateIdSet.add(d.id))
  })

  const seen = {}
  Object.keys(allByQuestionId).forEach((questionId) => {
    seen[questionId] = allByQuestionId[questionId].map((d) => {
      const base = {
        id: d.id,
        questionId: d.questionId,
        upvotes: d.upvotes,
        downvotes: d.downvotes,
        hasTextHtml: d.hasTextHtml,
        hasText: d.hasText,
        recencyMs: d.recencyMs,
        idSortKey: d.idSortKey
      }
      if (duplicateIdSet.has(d.id)) {
        base.text = d.text || ''
        base.textHtml = d.textHtml || ''
        base.bodyLoaded = true
      } else {
        base.bodyLoaded = false
      }
      return base
    })
  })

  const payload = {
    lastDocId: lastDocId.value,
    scannedCount: scannedCount.value,
    scanComplete: scanComplete.value,
    groups: duplicateGroups.value,
    seen,
    savedAt: Date.now()
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch (error) {
    console.warn('Could not persist dedupe progress (localStorage full?):', error)
    // Retry without any bodies — preview can lazy-load on expand
    try {
      Object.keys(seen).forEach((qid) => {
        seen[qid] = seen[qid].map(({ text, textHtml, ...meta }) => meta)
      })
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...payload, seen, groups: [] }))
      rebuildDuplicateGroupsFromMap()
    } catch (retryError) {
      console.warn('Persist retry also failed:', retryError)
    }
  }
}

const clearPersistedProgress = () => {
  localStorage.removeItem(STORAGE_KEY)
}

const hydrateDocBodies = async (docs) => {
  const missing = docs.filter((d) => !d.bodyLoaded)
  if (missing.length === 0) return docs

  await Promise.all(
    missing.map(async (d) => {
      try {
        const snap = await getDoc(doc(db, EXPLANATIONS, d.id))
        if (!snap.exists()) return
        const summarized = summarizeDoc(snap.id, snap.data() || {})
        Object.assign(d, summarized)
      } catch (error) {
        console.warn('Failed to hydrate explanation', d.id, error)
      }
    })
  )
  return docs
}

const loadPersistedProgress = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return
    const data = JSON.parse(raw)
    lastDocId.value = data.lastDocId || null
    scannedCount.value = data.scannedCount || 0
    scanComplete.value = Boolean(data.scanComplete)

    Object.keys(allByQuestionId).forEach((k) => delete allByQuestionId[k])

    if (data.seen && typeof data.seen === 'object') {
      Object.keys(data.seen).forEach((questionId) => {
        allByQuestionId[questionId] = (data.seen[questionId] || []).map((d) => ({
          ...d,
          text: d.text || '',
          textHtml: d.textHtml || '',
          bodyLoaded: Boolean(d.bodyLoaded)
        }))
      })
      rebuildDuplicateGroupsFromMap()
    } else if (Array.isArray(data.groups)) {
      duplicateGroups.value = data.groups
      data.groups.forEach((g) => {
        allByQuestionId[g.questionId] = g.docs.map((d) => ({ ...d, bodyLoaded: d.bodyLoaded !== false }))
      })
    }

    duplicateGroups.value.forEach((g) => {
      expanded[g.questionId] = false
    })
  } catch (error) {
    console.warn('Could not restore dedupe progress:', error)
  }
}

const refreshCount = async () => {
  loadingCount.value = true
  try {
    const snap = await getCountFromServer(collection(db, EXPLANATIONS))
    totalCount.value = snap.data().count
  } catch (error) {
    console.error('Error counting explanations:', error)
    scanError.value = error.message || 'Failed to load count'
  } finally {
    loadingCount.value = false
  }
}

const toggleGroup = async (questionId) => {
  const opening = !expanded[questionId]
  expanded[questionId] = opening
  if (!opening) return

  const group = duplicateGroups.value.find((g) => g.questionId === questionId)
  if (!group) return
  await hydrateDocBodies(group.docs)
  // Ensure map stays in sync for persist
  allByQuestionId[questionId] = [...group.docs]
  persistProgress()
}

const toggleSelect = (id, checked) => {
  const next = new Set(selectedSet.value)
  if (checked) next.add(id)
  else next.delete(id)
  selectedSet.value = next
}

const removeDocFromState = (docId) => {
  const nextSelected = new Set(selectedSet.value)
  nextSelected.delete(docId)
  selectedSet.value = nextSelected

  Object.keys(allByQuestionId).forEach((questionId) => {
    const docs = allByQuestionId[questionId]
    if (!docs) return
    const filtered = docs.filter((d) => d.id !== docId)
    if (filtered.length === 0) {
      delete allByQuestionId[questionId]
    } else {
      allByQuestionId[questionId] = filtered
    }
  })

  rebuildDuplicateGroupsFromMap()
  persistProgress()
}

const confirmDelete = async (message) => {
  const result = await swal.fire({
    title: 'Delete explanation?',
    text: message,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc2626',
    confirmButtonText: 'Yes, delete',
    cancelButtonText: 'Cancel'
  })
  return result.isConfirmed
}

const deleteOne = async (docId, questionId) => {
  const ok = await confirmDelete(`Permanently delete explanations/${docId} (questionId: ${questionId})?`)
  if (!ok) return

  deleting.value = true
  try {
    await deleteDoc(doc(db, EXPLANATIONS, docId))
    removeDocFromState(docId)
    if (typeof totalCount.value === 'number') totalCount.value = Math.max(0, totalCount.value - 1)
    await swal.fire('Deleted', 'Explanation document removed.', 'success')
  } catch (error) {
    console.error('Delete failed:', error)
    await swal.fire('Error', error.message || 'Delete failed', 'error')
  } finally {
    deleting.value = false
  }
}

const deleteSelectedBatch = async () => {
  const ids = selectedIds.value
  if (ids.length === 0) return

  const ok = await confirmDelete(`Permanently delete ${ids.length} selected explanation document(s)?`)
  if (!ok) return

  deleting.value = true
  let deleted = 0
  try {
    // Individual deletes (not writeBatch) so partial progress survives failures; write cost is the same.
    for (const id of ids) {
      await deleteDoc(doc(db, EXPLANATIONS, id))
      removeDocFromState(id)
      deleted += 1
      if (typeof totalCount.value === 'number') totalCount.value = Math.max(0, totalCount.value - 1)
    }
    await swal.fire('Deleted', `Removed ${deleted} document(s).`, 'success')
  } catch (error) {
    console.error('Batch delete failed:', error)
    await swal.fire('Error', `Stopped after ${deleted} delete(s): ${error.message || 'unknown error'}`, 'error')
  } finally {
    deleting.value = false
  }
}

const ingestPageDocs = (docs) => {
  docs.forEach((snap) => {
    const raw = snap.data() || {}
    const summarized = summarizeDoc(snap.id, raw)
    const qid = summarized.questionId
    if (!qid) return
    if (!allByQuestionId[qid]) allByQuestionId[qid] = []
    // Avoid duplicating if resume overlaps
    if (!allByQuestionId[qid].some((d) => d.id === summarized.id)) {
      allByQuestionId[qid].push(summarized)
    }
  })
  rebuildDuplicateGroupsFromMap()
}

const startOrResumeScan = async () => {
  if (scanning.value) return
  scanning.value = true
  stopRequested.value = false
  scanError.value = ''

  try {
    // Fresh scan only when there is no resume cursor and no in-progress state
    if (!lastDocId.value && scannedCount.value === 0) {
      Object.keys(allByQuestionId).forEach((k) => delete allByQuestionId[k])
      duplicateGroups.value = []
      scanComplete.value = false
    } else if (scanComplete.value && !lastDocId.value) {
      // Explicit re-scan after a completed run: clear and start over
      Object.keys(allByQuestionId).forEach((k) => delete allByQuestionId[k])
      duplicateGroups.value = []
      scannedCount.value = 0
      scanComplete.value = false
    } else {
      scanComplete.value = false
    }

    const colRef = collection(db, EXPLANATIONS)

    while (!stopRequested.value) {
      let pageQuery
      if (lastDocId.value) {
        const cursorSnap = await getDoc(doc(db, EXPLANATIONS, lastDocId.value))
        if (!cursorSnap.exists()) {
          scanError.value = `Resume cursor doc ${lastDocId.value} no longer exists. Reset progress and scan again.`
          break
        }
        pageQuery = query(
          colRef,
          orderBy(documentId()),
          startAfter(cursorSnap),
          limit(PAGE_SIZE)
        )
      } else {
        pageQuery = query(colRef, orderBy(documentId()), limit(PAGE_SIZE))
      }

      const snapshot = await getDocs(pageQuery)
      if (snapshot.empty) {
        scanComplete.value = true
        lastDocId.value = null
        persistProgress()
        break
      }

      ingestPageDocs(snapshot.docs)
      scannedCount.value += snapshot.docs.length
      lastDocId.value = snapshot.docs[snapshot.docs.length - 1].id
      persistProgress()

      if (snapshot.docs.length < PAGE_SIZE) {
        scanComplete.value = true
        lastDocId.value = null
        persistProgress()
        break
      }
    }
  } catch (error) {
    console.error('Scan failed:', error)
    scanError.value = error.message || 'Scan failed'
  } finally {
    scanning.value = false
    stopRequested.value = false
  }
}

const stopScan = () => {
  stopRequested.value = true
}

const resetScanProgress = async () => {
  const result = await swal.fire({
    title: 'Reset scan progress?',
    text: 'Clears local resume cursor and cached duplicate groups. Does not delete Firestore docs.',
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Reset'
  })
  if (!result.isConfirmed) return

  stopRequested.value = true
  scanning.value = false
  lastDocId.value = null
  scannedCount.value = 0
  scanComplete.value = false
  scanError.value = ''
  selectedSet.value = new Set()
  Object.keys(allByQuestionId).forEach((k) => delete allByQuestionId[k])
  Object.keys(expanded).forEach((k) => delete expanded[k])
  duplicateGroups.value = []
  clearPersistedProgress()
}

onMounted(async () => {
  loadPersistedProgress()
  await refreshCount()
})

onBeforeUnmount(() => {
  stopRequested.value = true
})
</script>
